import { forwardRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { GetDrawDownRepaymentScheduleCreditFacilityResponse } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import { addWeeks } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import BalanceAfterTopup from './balanceAfterTopup/BalanceAfterTopup';
import styles from './EarlyRepayment.module.css';
import { differenceBetweenDates, addMonths } from './EarlyRepaymentHelper';
import Fees from './fees/Fees';
import InterestOnLoan from './interestOnLoan/InterestOnLoan';
import Savings from './savings/Savings';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { CalendarSVG } from '../../../../svgComponents/calendarSVG/CalendarSVG';
import { InformationSVG } from '../../../../svgComponents/informationSVG/InformationSVG';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { MyOffersError } from '../../MyOffers.types';
import approvalStyles from '../flexiApprovedView/FlexiApprovedView.module.css';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showSchedule: (
        schedule: GetDrawDownRepaymentScheduleCreditFacilityResponse
    ) =>
        dispatch({
            type: SHOW_REPAYMENT_SCHEDULE_MODAL,
            customSchedule: schedule,
            source: 'Early repayment calculator'
        })
});

const EarlyRepayment = ({
    drawdownAmount,
    offerId,
    productType,
    loanDuration,
    maxDate,
    showSchedule,
    setErrors,
    earlyRepaymentCalculatorRef
}: {
    drawdownAmount: number;
    offerId: string | null;
    loanDuration: number;
    maxDate: string;
    productType: string;
    setErrors?: React.Dispatch<React.SetStateAction<MyOffersError[]>>;
    earlyRepaymentCalculatorRef?: React.MutableRefObject<null>;
} & ReturnType<typeof mapDispatchToProps>) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [activePreset, setActivePreset] = useState<string | null>(null);
    const [difference, setDifference] = useState(null);
    const [currentDate, setCurrentDate] = useState<string | null>(null);

    const stateKey = useStateKey();

    const minDate = new Date().toISOString().slice(0, 10);

    let earlyScheduleSummary: GetDrawDownRepaymentScheduleCreditFacilityResponse['data']['summary'];
    let totalSaving = 0;
    let totalInterest = 0;
    let totalBalance = 0;
    let totalFees = 0;

    const { isLoading, data: earlyDrawdownRepaymentSchedule } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            currentDate,
            productType,
            offerId
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${productType}/${offerId}/?amount=${drawdownAmount}&early_final_repayment_date=${currentDate}`
            );
            return response.json();
        },
        enabled: Boolean(currentDate)
    });

    if (earlyDrawdownRepaymentSchedule?.data) {
        earlyScheduleSummary = earlyDrawdownRepaymentSchedule.data.summary;
        totalInterest = earlyScheduleSummary!.total_interest!;
        totalBalance = earlyScheduleSummary!.total_payable!;
        totalFees = earlyScheduleSummary!.total_fees!;
        totalSaving = earlyScheduleSummary!.amount_saved!;
    }

    const handleDateChange = (date: string, label?: string) => {
        if (setErrors && !offerId) {
            const error: MyOffersError = {
                source: 'Early Repayment Calculator',
                type: 'Offer Not Selected'
            };
            setErrors((prev) => [...prev, error]);
        } else {
            //dont make api call if date is not yet a valid date- user may be typing in a date.
            //Used minDate comparison to determine valid date
            const parsedDate = Date.parse(date);
            if (parsedDate < Date.parse(minDate)) {
                setEndDate(date);
            } else {
                setShowBreakdown(true);
                setCurrentDate(date);
                setEndDate(date);
                //@ts-expect-error
                setDifference(differenceBetweenDates(date));
                //amplitude tracking
                tracking.ampli.earlyRepaymentCalculatorUsed({
                    early_repayment_date: date,
                    preset_button: String(label)
                });
            }
        }
    };

    const renderEarlyBreakdownTitle = () => {
        //@ts-ignore
        const [years, months, days] = difference;

        return years > 0 ? (
            <FormattedMessage
                id="_.choose_amount.early_repayment.breakdown_title_years"
                defaultMessage="If you repay in {years} year, {months} months and {days} days..."
                values={{ years, months, days }}
            />
        ) : months > 0 && days > 0 ? (
            <FormattedMessage
                id="_.choose_amount.early_repayment.breakdown_title_months_and_days"
                defaultMessage="If you repay in {months} months and {days} days..."
                values={{ months, days }}
            />
        ) : months > 0 ? (
            <FormattedMessage
                id="_.choose_amount.early_repayment.breakdown_title_months"
                defaultMessage="If you repay in {months, plural, one {# month} other {# months}}..."
                values={{ months }}
            />
        ) : (
            <FormattedMessage
                id="_.choose_amount.early_repayment.breakdown_title_days"
                defaultMessage="If you repay in {days, plural, one {# day} other {# days}}..."
                values={{ days }}
            />
        );
    };

    const renderPresets = () => {
        const fourWeeks = addWeeks(new Date(), 4);
        const threeMonths = addMonths(3);
        const sixMonths = addMonths(6);

        const presetDates = [
            {
                key: 'early-repayment-4-weeks-preset',
                label: (
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.four_weeks"
                        defaultMessage="4 weeks"
                    />
                ),
                value: fourWeeks.toISOString().substr(0, 10)
            },
            {
                key: 'early-repayment-3-months-preset',
                label: (
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.three_months"
                        defaultMessage="3 months"
                    />
                ),
                value: threeMonths.toISOString().substr(0, 10)
            },
            {
                key: 'early-repayment-6-months-preset',
                label: (
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.six_months"
                        defaultMessage="6 months"
                    />
                ),
                value: sixMonths.toISOString().substr(0, 10)
            }
        ];

        return presetDates.map((preset) => (
            <Button
                variant={
                    activePreset === preset.key ? 'secondary' : 'secondary-alt'
                }
                onClick={() => {
                    setActivePreset(preset.key);
                    handleDateChange(
                        preset.value,
                        preset.label.props.defaultMessage
                    );
                }}
                data-tracking-id={preset.key}
                key={preset.key}
                data-action-name={preset.key}
            >
                {preset.label}
            </Button>
        ));
    };

    return (
        <section
            data-testid="multi-loan-dashboard-approved-early-repayment-simulation"
            ref={earlyRepaymentCalculatorRef}
        >
            <div className={styles.header}>
                <span className={styles.titleLineOne}>
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.titleLineOne"
                        defaultMessage="Interested in repaying early?"
                    />
                </span>

                <span className={styles.titleLineTwo}>
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.titleLineTwo"
                        defaultMessage="Here's how it could look"
                    />
                </span>
            </div>

            <div
                className={`${approvalStyles.approvalSection} ${styles.earlyRepaymentSectionOverrides}`}
            >
                <div className={styles.datePicker}>
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.repay"
                        defaultMessage="What if I repay early in full on:"
                    />
                    <input
                        data-testid="early-repayment-date-picker"
                        className={styles.dateInput}
                        type="date"
                        //@ts-ignore-error
                        value={endDate}
                        min={minDate} // todays date
                        max={maxDate} // 364 days in future
                        onChange={(e) => {
                            e.persist();
                            setActivePreset(null);
                            handleDateChange(e.target.value);
                        }}
                        data-tracking-id="early-repayment-calendar-input"
                        data-action-name="Early repayment calendar input"
                    />
                </div>

                <div className="flex flex-row items-center justify-start gap-s">
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.or_in"
                        defaultMessage="Or in..."
                    />
                    {renderPresets()}
                </div>

                {showBreakdown && (
                    <div
                        className={classnames(styles.earlyRepaymentBreakdown, {
                            [styles.loading]: isLoading
                        })}
                    >
                        <h3
                            className={styles.breakdownTitle}
                            data-testid="early-repayment-breakdown-title"
                        >
                            {renderEarlyBreakdownTitle()}
                        </h3>
                        <div className={styles.totals}>
                            <InterestOnLoan
                                totalInterest={totalInterest}
                                isLoading={isLoading}
                            />
                            <Fees totalFees={totalFees} isLoading={isLoading} />
                            <BalanceAfterTopup
                                totalBalance={totalBalance}
                                isLoading={isLoading}
                            />
                            <Savings
                                totalSaving={totalSaving}
                                isLoading={isLoading}
                            />
                        </div>
                        <Button
                            variant="secondary"
                            className={styles.showScheduleHPB}
                            onClick={() =>
                                showSchedule(earlyDrawdownRepaymentSchedule)
                            }
                            data-action-name="Show early repayment schedule"
                        >
                            <span
                                className={styles.linkHPB}
                                data-tracking-id="early-repayment-schedule-modal"
                            >
                                <CalendarSVG className={styles.calendar} />
                                <FormattedMessage
                                    id="_.choose_amount.early_repayment.example_repayment_schedule"
                                    defaultMessage="See example repayment schedule"
                                />
                            </span>
                        </Button>

                        <div className={styles.info}>
                            <InformationSVG className={styles.infoSVG} />
                            <FormattedMessage
                                id="_.choose_amount.early_repayment.info"
                                defaultMessage="Your repayment schedule will always be for {months} months but you can repay early in your dashboard at any time."
                                values={{ months: loanDuration }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

const EarlyRepaymentCalculator = forwardRef(EarlyRepayment);
// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(EarlyRepaymentCalculator);
